import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { fetchCustomer, createCustomer, updateCustomer } from '@/lib/api/customerApi';
import { fetchVehiclesByCustomer } from '@/lib/api/vehicleApi';
import type { Customer, CustomerVehicle, ServiceRecommendation } from '@/lib/customers/types';
import { CUSTOMER_SEARCH_MIN_CHARS, runCustomerSearch } from '@/lib/search/customerSearchContract';

interface CustomerWithRelations extends Customer {
  vehicles: CustomerVehicle[];
  recommendations: ServiceRecommendation[];
}

interface CreateCustomerInput {
  full_name: string;
  phone?: string | null;
  email?: string | null;
  address?: Record<string, string | number | boolean | null> | null;
  notes?: string | null;
  sms_opt_in?: boolean;
  email_opt_in?: boolean;
  preferred_channel?: 'sms' | 'email' | 'phone' | null;
  fallback_channel?: 'sms' | 'email' | 'phone' | null;
}

interface UpdateCustomerInput extends Partial<CreateCustomerInput> {
  id: string;
}

/**
 * Hook for fetching and managing a single customer with related data.
 * Uses customer-read and vehicle-read edge functions for encrypted PII.
 */
export function useCustomer(customerId: string | undefined) {
  const { activeShop } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['customer', customerId],
    queryFn: async (): Promise<CustomerWithRelations | null> => {
      if (!customerId || !activeShop?.id) return null;

      // Fetch customer via edge function (decrypts PII server-side)
      const customer = await fetchCustomer({
        customer_id: customerId,
        shop_id: activeShop.id,
      });

      if (!customer) return null;

      // Fetch vehicles (via edge function) and recommendations in parallel
      const [vehicles, recommendationsResult] = await Promise.all([
        fetchVehiclesByCustomer(activeShop.id, customerId),
        // FIX-R30-M-1: explicit column list — service_recommendations
        // has no PII today, but `*` would auto-expose any future columns
        // (e.g. internal ML scores, operator notes).
        supabase
          .from('service_recommendations')
          .select(`
            id,
            shop_id,
            customer_id,
            vehicle_id,
            source_type,
            source_ticket_id,
            source_finding_group_id,
            job_catalog_id,
            title,
            description,
            notes,
            severity,
            status,
            recommended_at,
            resolved_at,
            expires_at,
            converted_ticket_id,
            converted_at,
            created_at,
            updated_at
          `)
          .eq('customer_id', customerId)
          .eq('shop_id', activeShop.id)
          .order('recommended_at', { ascending: false }),
      ]);

      if (recommendationsResult.error) throw recommendationsResult.error;

      return {
        ...customer,
        vehicles: vehicles as unknown as CustomerVehicle[],
        recommendations: recommendationsResult.data || [],
      } as CustomerWithRelations;
    },
    enabled: !!customerId && !!activeShop?.id,
  });

  const createMutation = useMutation({
    mutationFn: async (input: CreateCustomerInput): Promise<{ id: string }> => {
      if (!activeShop?.id) throw new Error('No active shop');

      const customer_id = await createCustomer({
        shop_id: activeShop.id,
        full_name: input.full_name,
        phone: input.phone ?? null,
        email: input.email ?? null,
        address: input.address ?? null,
        notes: input.notes ?? null,
        sms_opt_in: input.sms_opt_in ?? false,
        email_opt_in: input.email_opt_in ?? false,
        preferred_channel: input.preferred_channel ?? null,
        fallback_channel: input.fallback_channel ?? null,
      });

      return { id: customer_id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers-list'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (input: UpdateCustomerInput): Promise<{ id: string }> => {
      if (!activeShop?.id) throw new Error('No active shop');
      const { id, ...updates } = input;

      const customer_id = await updateCustomer({
        customer_id: id,
        shop_id: activeShop.id,
        ...updates,
      });

      return { id: customer_id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customer', data.id] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers-list'] });
    },
  });

  return {
    customer: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    createCustomer: createMutation.mutateAsync,
    updateCustomer: updateMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
}

/**
 * Hook for fetching customer list — uses customer-list edge function
 * which returns masked PII (not plaintext).
 */
export function useCustomers(searchQuery?: string) {
  const { activeShop } = useAuth();

  return useQuery({
    queryKey: ['customers-list', activeShop?.id, searchQuery],
    queryFn: async () => {
      if (!activeShop?.id) return [];

      // If search query present, use customer-search edge function — through the one search contract
      // (Core-100 R4 wave 3, CORE-AY D): a failed search is an ERROR, never an empty list.
      if (searchQuery && searchQuery.trim().length >= CUSTOMER_SEARCH_MIN_CHARS) {
        const outcome = await runCustomerSearch(supabase, activeShop.id, searchQuery, 100);
        if (!outcome.ok) throw new Error(outcome.reason ?? 'Customer search failed');
        return outcome.results;
      }

      // Otherwise use customer-list for masked list view
      const { data, error } = await supabase.functions.invoke('customer-list', {
        body: {
          shop_id: activeShop.id,
          limit: 100,
          order_by: 'full_name',
          ascending: true,
        },
      });

      if (error) throw new Error(error.message);
      return data?.customers || [];
    },
    enabled: !!activeShop?.id,
  });
}

